/**
 * The rule that keeps one job photo or video from being linked twice to the same evidence
 * collection on a SOR line. Pure, with no I/O, so the task, contractor and bulk routes (and the
 * sibling AttachItemEvidence) apply the same rule and the same wording.
 */
import { Attachment } from './attachment';
import { SOR_EVIDENCE_TASK_FIELDS } from './sor.evidence';
import { Task } from '../tasks/task';
import { TaskLinkType } from '../tasks/task.enum';

const SOR_EVIDENCE_FIELD_NAMES: readonly string[] = Object.values(SOR_EVIDENCE_TASK_FIELDS);

/**
 * True only for a SOR line's main task and one of its three evidence fields. Callers ask this
 * first; when false they run exactly as before, so job tasks and `additionalFiles` keep repeats.
 */
export function isSorEvidenceLinkTarget(task: Pick<Task, 'type'> | null | undefined, field: string): boolean {

    return task?.type === TaskLinkType.Item && SOR_EVIDENCE_FIELD_NAMES.includes(field);
}

/**
 * Splits incoming attachments into the ones to link and the ones already in the collection, by
 * `_id` only. An entry without `_id` is an upload and is always linked. A repeat inside the
 * request is dropped and not counted, because it is not "already on that SOR". `link` keeps the
 * incoming order. Ids are compared as strings because a stored `_id` can come back ObjectId-like.
 */
export function partitionEvidenceLinks(existing: Attachment[] | null | undefined, incoming: Attachment[]): { link: Attachment[]; alreadyLinked: Attachment[] } {

    const storedIds = new Set((existing ?? []).filter(item => item?._id != null).map(item => String(item._id)));
    const seenIds = new Set<string>();
    const link: Attachment[] = [];
    const alreadyLinked: Attachment[] = [];

    for (const attachment of incoming) {
        if (attachment?._id == null) {
            link.push(attachment);
            continue;
        }

        const id = String(attachment._id);

        if (seenIds.has(id)) {
            continue;
        }

        seenIds.add(id);

        if (storedIds.has(id)) {
            alreadyLinked.push(attachment);
            continue;
        }

        link.push(attachment);
    }

    return { link, alreadyLinked };
}

/** The one sentence every surface shows for skipped links; undefined when nothing was skipped. */
export function describeAlreadyLinked(n: number): string | undefined {

    if (n <= 0) {
        return undefined;
    }

    if (n === 1) {
        return '1 link skipped, already on that SOR';
    }

    return `${n} links skipped, already on that SOR`;
}
